import {
  FILTER_QUERY,
  FILTER_RECEIVE,
  FILTER_TOGGLE_MOBILE,
  FILTER_EXPAND_SECTION,
  FILTER_CHANGE_ACTION,
  FILTER_FETCH_FAILED,
  COMPARE_PROPERTY,
  COMPARE_PROPERTIES,
  SET_HORIZONTAL_FILTER,
} from '../actions/filter';
import { LOCAL_STORAGE_FILTER_KEY } from '../constants';
import { trackFilterLayoutChange } from '../tracking';

const hasChangedCategory = (action, state) =>
  action.categoryData.categoryId !== state.loadedCategoryId ||
  (action.categoryData.categoryId === state.loadedCategoryId &&
    state.toggledBrandCategory);

const getSelectionFromQuery = (
  { type, categoryId, ...query },
  currentlySelected
) => {
  const filterIds = Object.keys(query).reduce((all, sectionId) => {
    const values = query[sectionId];
    return Array.isArray(values)
      ? values.map(valueId => `${sectionId}-${valueId}`).concat(all)
      : all.concat(sectionId);
  }, []);

  const stillSelected = Object.keys(currentlySelected)
    .filter(key => filterIds.includes(key))
    .reduce(
      (obj, key) => ({
        ...obj,
        [key]: currentlySelected[key],
      }),
      {}
    );

  return { stillSelected, query };
};

const getSelection = ({ id, value, checked, filterType, ...rest }) => {
  const data = {
    id,
    value,
    checked,
    filterType,
    ...rest,
  };

  switch (filterType) {
    case 'list':
      return { [`${id}-${value}`]: checked ? data : null };
    case 'boolean':
    case 'slider':
    default:
      return {
        [id]: data,
      };
  }
};

export default (state, action) => {
  const initialState = {
    selected: [],
    categoryId: null,
    isBrandCategory: false,
    toggledBrandCategory: false,
    loadedCategoryId: undefined,
    query: {},
    definition: [],
    values: {},
    core: [],
    expandedCategories: [],
    mobileOpen: false,
    error: undefined,
    comparing: [],
    showFilter: false,
    productCount: null,
    showHorizontalFilter: undefined,
  };

  if (!state) {
    return initialState;
  }

  switch (action.type) {
    case 'ROUTE_LOADED': {
      // NOTE: Reset filter on category receive, noop if same category is received
      if (!action.route || action.route.name !== 'category') {
        return initialState;
      }
      // Abort if category data is missing (404)
      if (!action.data || !action.data.category) {
        return initialState;
      }

      const { category } = action.data;
      const categoryId = category && category.id;

      if (state.categoryId === categoryId) {
        return {
          ...state,
          isBrandCategory: !!category.brand,
          toggledBrandCategory:
            (!category.brand && state.isBrandCategory) ||
            (category.brand && !state.isBrandCategory),
        };
      }

      return {
        ...initialState,
        showHorizontalFilter: state.showHorizontalFilter,
        categoryId,
        isBrandCategory: !!category.brand,
        toggledBrandCategory:
          (!category.brand && state.isBrandCategory) ||
          (category.brand && !state.isBrandCategory),
        comparing:
          category &&
          category.products &&
          category.products.pageInfo &&
          category.products.pageInfo.selectedProperties &&
          category.products.pageInfo.selectedProperties
            .map(x => x.id)
            .slice(0, 3),
      };
    }
    case FILTER_CHANGE_ACTION: {
      // When clearing all fields for a section the action data
      // is a new query with the cleared sections removed.
      // Update selection tags to match this.
      if (!action.id) {
        const { stillSelected, query } = getSelectionFromQuery(
          action,
          state.selected
        );

        return {
          ...state,
          selected: stillSelected,
          query,
        };
      }

      return {
        ...state,
        selected: { ...state.selected, ...getSelection(action) },
      };
    }
    case FILTER_EXPAND_SECTION: {
      if (state.expandedCategories.includes(action.name)) {
        return {
          ...state,
          expandedCategories: state.expandedCategories.filter(
            name => name !== action.name
          ),
        };
      }

      return {
        ...state,
        expandedCategories: [...state.expandedCategories, action.name],
      };
    }

    case FILTER_TOGGLE_MOBILE: {
      return {
        ...state,
        mobileOpen: action.open,
      };
    }
    case FILTER_RECEIVE: {
      const newState = {
        ...state,
        ...action.categoryData,
        showFilter: true,
        error: undefined,
      };

      if (action.data.item) {
        newState.values = {
          ...state.values,
          ...action.data.item,
        };
      }

      if (
        // NOTE: Skip this update if initial load is done
        hasChangedCategory(action, state) &&
        action.data.meta &&
        action.data.meta.property_definitions
      ) {
        const { meta } = action.data;
        const coreProps = new Array(meta.core_properties.length);

        // Recursively create a new array of filter sections based on `property_definitions`
        // excluding core property sections but includes the core properties children.
        const createSubProperties = properties =>
          properties
            .map(property => {
              if (meta.core_properties.includes(property.id)) {
                const index = meta.core_properties.indexOf(property.id);

                // We need to save the property `child` in its matching `property` index
                // to maintain the correct order of coreProperties
                // Sub properties `children` that are not marked as core are moved to its
                // parent section. So we can null it here
                coreProps[index] = { ...property, children: null };

                if (property.children) {
                  return createSubProperties(property.children);
                }

                return null;
              }

              return property;
            })
            .filter(p => p)
            .flatten(); // from core-js

        const defs = meta.property_definitions.map(def => ({
          ...def,
          children: createSubProperties(def.children),
        }));

        const first = coreProps.filter(p => ['brands', 'price'].includes(p.id));
        const rest = coreProps.filter(p => !['brands', 'price'].includes(p.id));

        newState.core = [...first, ...rest];
        newState.definition = defs;
      }

      if (
        // NOTE: Skip this update if initial load is done
        hasChangedCategory(action, state) &&
        action.data.meta
      ) {
        newState.query = action.data.meta.filter_settings || {};
        // Brand ids are strings in some API responses and numbers in others. That should be fixed, but it's probably part of a bigger filter overhaul...
        // In order to support strict equality checks in client-side operations, we just convert them to strings here for now.
        if (newState.query.brands) {
          newState.query.brands = newState.query.brands.map(id =>
            id.toString()
          );
        }
      }

      newState.loadedCategoryId = action.categoryData.categoryId;

      return newState;
    }
    case FILTER_FETCH_FAILED: {
      return {
        ...state,
        error: action.error,
      };
    }
    case FILTER_QUERY: {
      return {
        ...state,
        query: action.query,
      };
    }

    case 'LOCAL_STORAGE_RECEIVE': {
      const filterStateFromLocalStorage =
        action.localStorageState[LOCAL_STORAGE_FILTER_KEY];

      if (filterStateFromLocalStorage) {
        return {
          ...state,
          showHorizontalFilter: filterStateFromLocalStorage,
        };
      }

      return state;
    }

    case SET_HORIZONTAL_FILTER: {
      trackFilterLayoutChange(action.show ? 'vertical' : 'horizontal');
      return {
        ...state,
        showHorizontalFilter: action.show,
      };
    }

    case COMPARE_PROPERTIES: {
      return {
        ...state,
        comparing: action.propertyIds,
      };
    }
    case COMPARE_PROPERTY: {
      const selected = state.comparing.includes(action.propertyId);

      const comparing = selected
        ? state.comparing.filter(x => x !== action.propertyId)
        : state.comparing.concat(action.propertyId);

      return {
        ...state,
        comparing: [...new Set(comparing)],
      };
    }
    default: {
      return state;
    }
  }
};
